// Renders synthetic EKG rhythm strips as inline SVG.
// This guarantees content always loads (no external hotlinks) and lets
// click-quiz target coordinates line up exactly with drawn waveforms.

const WIDTH = 1200;
const HEIGHT = 320;
const BASELINE = HEIGHT / 2;

// Wave building block: one PQRST beat centered at x0
function beat(x0, opts = {}) {
  const {
    pAmp = 8,
    qAmp = 8,
    rAmp = 90,
    sAmp = 25,
    tAmp = 22,
    prGap = 42,
    stGap = 24,
  } = opts;
  const y = BASELINE;
  const p = `Q ${x0 - 46},${y - pAmp} ${x0 - 32},${y}`;
  const iso1 = `L ${x0 - prGap + 20},${y}`;
  const q = `L ${x0 - 4},${y + qAmp}`;
  const r = `L ${x0},${y - rAmp}`;
  const s = `L ${x0 + 6},${y + sAmp}`;
  const iso2 = `L ${x0 + stGap},${y}`;
  const t = `Q ${x0 + stGap + 22},${y - tAmp} ${x0 + stGap + 44},${y}`;
  return `${p} ${iso1} ${q} ${r} ${s} ${iso2} ${t}`;
}

function buildPath(commands) {
  // Prepend M for the first move
  return "M 0," + BASELINE + " L 40," + BASELINE + " " + commands.join(" ") + ` L ${WIDTH},${BASELINE}`;
}

function sinusPath() {
  // 6 beats evenly spaced -> ~75 bpm feel
  const beats = [];
  for (let i = 0; i < 6; i++) {
    const x = 140 + i * 180;
    beats.push(beat(x));
    if (i < 5) beats.push(`L ${x + 90},${BASELINE}`);
  }
  return buildPath(beats);
}

function afibPath() {
  // Irregular R-R, chaotic wavy baseline (no P), narrow QRS
  const xs = [130, 260, 340, 520, 640, 820, 920, 1080];
  const beats = [];
  xs.forEach((x, i) => {
    beats.push(beat(x, { pAmp: 0, rAmp: 80, tAmp: 15 }));
    // Between beats: chaotic squiggle instead of flat baseline
    const nextX = xs[i + 1] || WIDTH - 40;
    let cx = x + 50;
    while (cx < nextX - 60) {
      const wobble = (Math.sin(cx * 0.7) + Math.cos(cx * 0.9)) * 4;
      beats.push(`L ${cx},${BASELINE + wobble}`);
      cx += 12;
    }
  });
  return buildPath(beats);
}

function vtachPath() {
  // Ventricular Tachycardia:
  //  - WIDE monomorphic QRS (visibly ≥ 3 small boxes wide vs the sinus/SVT strips)
  //  - regular, ~150–180 bpm, NO P waves
  //  - slurred upstroke and slurred downstroke; fused ST-T
  //  - deep wide S wave; no isoelectric baseline between beats
  const beats = [];
  const spacing = 165;
  for (let i = 0; i < 7; i++) {
    const x = 90 + i * spacing;
    beats.push(
      // isoelectric approach
      `L ${x - 55},${BASELINE + 4}`,
      // gentle initial downstroke (Q-like)
      `L ${x - 40},${BASELINE + 20}`,
      // SLOW slurred upstroke to R peak
      `L ${x - 8},${BASELINE - 90}`,
      // BROAD flat R peak (this is what gives VT its wide look)
      `L ${x + 10},${BASELINE - 92}`,
      // SLOW slurred downstroke into deep S
      `L ${x + 30},${BASELINE + 55}`,
      // wide slurred return (T fused with S)
      `L ${x + 55},${BASELINE + 6}`,
    );
  }
  return buildPath(beats);
}

function stemiPath() {
  // Sinus with obvious ST elevation
  const beats = [];
  for (let i = 0; i < 6; i++) {
    const x = 140 + i * 180;
    const elev = 24; // ST elevation amount
    beats.push(
      `Q ${x - 46},${BASELINE - 8} ${x - 32},${BASELINE}`,
      `L ${x - 22},${BASELINE}`,
      `L ${x - 4},${BASELINE + 8}`,
      `L ${x},${BASELINE - 90}`,
      `L ${x + 6},${BASELINE + 20}`,
      // ST segment ELEVATED
      `L ${x + 18},${BASELINE - elev}`,
      `L ${x + 40},${BASELINE - elev}`,
      // T wave from elevated ST
      `Q ${x + 60},${BASELINE - elev - 20} ${x + 80},${BASELINE}`,
    );
    if (i < 5) beats.push(`L ${x + 90},${BASELINE}`);
  }
  return buildPath(beats);
}

function svtPath() {
  // Narrow-complex tachycardia ~180 bpm, no visible P waves, regular
  const beats = [];
  const spacing = 110;
  for (let i = 0; i < 10; i++) {
    const x = 90 + i * spacing;
    // No P wave — straight into narrow QRS
    beats.push(
      `L ${x - 6},${BASELINE + 8}`,
      `L ${x},${BASELINE - 85}`,
      `L ${x + 6},${BASELINE + 20}`,
      `L ${x + 16},${BASELINE}`,
      // Small T
      `Q ${x + 34},${BASELINE - 14} ${x + 52},${BASELINE}`,
    );
  }
  return buildPath(beats);
}

function aflutterPath() {
  // Sawtooth F waves at ~300/min with 2:1 conduction -> QRS every 2nd F
  const commands = [];
  // 12 F-wave sawtooth peaks, QRS after every 2nd peak (6 QRSs)
  let x = 40;
  const step = 90; // spacing between F-wave peaks
  for (let i = 0; i < 12; i++) {
    // Sawtooth peak
    commands.push(`L ${x + step * 0.4},${BASELINE - 22}`); // up
    commands.push(`L ${x + step},${BASELINE + 6}`); // down
    if (i % 2 === 1) {
      // Insert a QRS after every 2nd F peak
      const qx = x + step + 24;
      commands.push(
        `L ${qx - 4},${BASELINE + 10}`,
        `L ${qx},${BASELINE - 70}`,
        `L ${qx + 6},${BASELINE + 20}`,
        `L ${qx + 16},${BASELINE}`,
      );
      x = qx + 16;
    } else {
      x = x + step;
    }
  }
  return buildPath(commands);
}

function lbbbPath() {
  // Left Bundle Branch Block in a lateral lead (Lead I / V6 view):
  //  - Wide QRS (≥ 0.12 s)
  //  - BROAD MONOMORPHIC (single-peak) R wave with a slight mid-notch — NOT rsR'
  //  - NO initial q wave (septal q is lost)
  //  - Discordant T wave (T points OPPOSITE the dominant QRS direction → inverted here)
  //  - P wave preserved (sinus with LBBB conduction)
  const beats = [];
  for (let i = 0; i < 5; i++) {
    const x = 170 + i * 200;
    beats.push(
      // Small P wave
      `Q ${x - 96},${BASELINE - 8} ${x - 78},${BASELINE}`,
      // Flat PR segment
      `L ${x - 52},${BASELINE}`,
      // WIDE slurred upstroke straight into a broad, tall monomorphic R (no q, no rSR')
      `L ${x - 30},${BASELINE - 50}`,
      `L ${x - 10},${BASELINE - 88}`,
      // slight notch on top of the broad R (LBBB "M" is subtle, not two full peaks)
      `L ${x - 2},${BASELINE - 80}`,
      `L ${x + 8},${BASELINE - 88}`,
      // wide slurred downstroke
      `L ${x + 30},${BASELINE - 40}`,
      `L ${x + 50},${BASELINE + 10}`,
      // DISCORDANT (inverted) T wave — opposite polarity to main QRS
      `Q ${x + 76},${BASELINE + 34} ${x + 100},${BASELINE + 6}`,
      `L ${x + 130},${BASELINE}`,
    );
  }
  return buildPath(beats);
}

function rbbbPath() {
  // Right Bundle Branch Block in V1 view:
  //  - Wide QRS (≥ 0.12 s)
  //  - rSR' "RABBIT EARS": small r, small s, then a second taller R'
  //  - Wide slurred T wave (often inverted in V1, but drawn upright here for clarity)
  //  - P wave preserved
  const beats = [];
  for (let i = 0; i < 5; i++) {
    const x = 170 + i * 200;
    beats.push(
      // P wave
      `Q ${x - 96},${BASELINE - 8} ${x - 78},${BASELINE}`,
      `L ${x - 52},${BASELINE}`,
      // Initial small r
      `L ${x - 36},${BASELINE - 30}`,
      // Small S wave (dip below baseline)
      `L ${x - 22},${BASELINE + 22}`,
      // Second taller R' — the classic "rabbit-ear"
      `L ${x - 6},${BASELINE - 70}`,
      // Down the second peak
      `L ${x + 12},${BASELINE + 18}`,
      // Wide slurred S wave return
      `L ${x + 34},${BASELINE + 4}`,
      // T wave (drawn upright / slightly inverted looking depending on rhythm interpretation)
      `Q ${x + 60},${BASELINE - 22} ${x + 86},${BASELINE}`,
      `L ${x + 120},${BASELINE}`,
    );
  }
  return buildPath(beats);
}

function wenckebachPath() {
  // Mobitz I: progressively lengthening PR then dropped QRS
  // Show 4-beat pattern: PR normal, longer, longer, then P without QRS
  const beats = [];
  // Cycle 1: 4 P waves, but 4th drops the QRS
  const cycleStart = 60;
  const prSchedule = [42, 60, 82, 110]; // PR increases; last has NO QRS
  let x = cycleStart;
  for (let cyc = 0; cyc < 2; cyc++) {
    for (let i = 0; i < 4; i++) {
      const pr = prSchedule[i];
      const px = x;
      // P wave
      beats.push(`Q ${px + 12},${BASELINE - 10} ${px + 24},${BASELINE}`);
      // PR interval flat
      beats.push(`L ${px + 24 + pr},${BASELINE}`);
      if (i < 3) {
        // QRS + T follows
        const qx = px + 24 + pr + 6;
        beats.push(
          `L ${qx},${BASELINE + 10}`,
          `L ${qx + 6},${BASELINE - 80}`,
          `L ${qx + 12},${BASELINE + 22}`,
          `L ${qx + 22},${BASELINE}`,
          `Q ${qx + 40},${BASELINE - 16} ${qx + 60},${BASELINE}`,
        );
        x = qx + 60 + 40; // gap before next P
      } else {
        // Dropped beat: no QRS, longer pause
        x = px + 24 + pr + 100;
        beats.push(`L ${x},${BASELINE}`);
      }
    }
  }
  return buildPath(beats);
}

const PATHS = {
  sinus: sinusPath,
  afib: afibPath,
  vtach: vtachPath,
  stemi: stemiPath,
  svt: svtPath,
  aflutter: aflutterPath,
  lbbb: lbbbPath,
  rbbb: rbbbPath,
  wenckebach: wenckebachPath,
  normal: sinusPath,
};

// Named landmarks in %-coords for annotation/click-target hints
export const LANDMARKS = {
  sinus: { p: { x: 0.075, y: 0.42 }, qrs: { x: 0.117, y: 0.35 }, t: { x: 0.17, y: 0.42 } },
};

export default function EkgStrip({ rhythm = "sinus", label, className = "", innerRef }) {
  const build = PATHS[rhythm] || PATHS.sinus;
  const d = build();
  return (
    <svg
      ref={innerRef}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className={className}
      preserveAspectRatio="none"
      style={{ display: "block", width: "100%", height: "100%" }}
    >
      {/* Fine grid */}
      <defs>
        <pattern id={`smallGrid-${rhythm}`} width="10" height="10" patternUnits="userSpaceOnUse">
          <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(34,197,94,0.08)" strokeWidth="0.5" />
        </pattern>
        <pattern id={`bigGrid-${rhythm}`} width="50" height="50" patternUnits="userSpaceOnUse">
          <rect width="50" height="50" fill={`url(#smallGrid-${rhythm})`} />
          <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(34,197,94,0.18)" strokeWidth="1" />
        </pattern>
      </defs>
      <rect width={WIDTH} height={HEIGHT} fill="#050908" />
      <rect width={WIDTH} height={HEIGHT} fill={`url(#bigGrid-${rhythm})`} />
      {label && (
        <text x="16" y="28" fill="rgba(34,197,94,0.7)" fontFamily="JetBrains Mono, monospace" fontSize="14">
          {label}
        </text>
      )}
      <path
        d={d}
        fill="none"
        stroke="#22c55e"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ filter: "drop-shadow(0 0 4px rgba(34,197,94,0.8))" }}
      />
    </svg>
  );
}
